/**
 * BusinessDayRangePicker — Monatskalender mit Bereichsauswahl fuer Zeitraum-
 * Dialoge (Urlaub, Krankmeldung, Abwesenheiten, Betriebsurlaub).
 *
 * - Tageszellen eingefaerbt nach Betriebskalender-Tagtyp (api.calendar.dayInfos,
 *   gecacht je Monat via useDayInfos) + kompakte Legende.
 * - Bereichsauswahl wie ein Slider: Klick = Start, zweiter Klick = Ende (Klick
 *   vor Start setzt neuen Start); zusaetzlich echtes Ziehen ueber PointerEvents
 *   (touch-tauglich, cross-Monat via elementFromPoint).
 * - Live-Zaehler: "X Betriebstage im Zeitraum (Y Tage uebersprungen)" —
 *   Betriebstage = isPlannable (ARBEITSTAG/BETRIEBSURLAUB); Halbtag-Gewichtung
 *   spiegelt leave_service.covered_business_days (0,5 nur wenn der Randtag
 *   selbst anrechenbar ist).
 * - Controlled: {from, to, onChange} — synchron mit den Datums-Inputs darueber.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import {
  DAY_TYPE_META,
  DAY_TYPE_ORDER,
  MONTH_NAMES,
  WEEKDAYS_SHORT,
  todayIso,
  weekdayIndex,
} from "@/components/calendar/dayTypes"
import { Button } from "@/components/ui/button"
import { fmtDays } from "@/lib/format"
import { isIsoDate, toDayMap, useDayInfos, useDayInfosRange } from "@/lib/useDayInfos"
import { cn } from "@/lib/utils"
import type { DayInfo } from "@/types"

const pad = (n: number) => String(n).padStart(2, "0")

interface YearMonth {
  year: number
  month: number
}

function ymOf(iso: string): YearMonth | null {
  if (!isIsoDate(iso)) return null
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) }
}

function addMonths(ym: YearMonth, delta: number): YearMonth {
  const idx = ym.year * 12 + (ym.month - 1) + delta
  return { year: Math.floor(idx / 12), month: ((idx % 12) + 12) % 12 + 1 }
}

function sameYm(a: YearMonth, b: YearMonth): boolean {
  return a.year === b.year && a.month === b.month
}

/** Alle Tage eines Monats als "YYYY-MM-DD" */
function monthDates(ym: YearMonth): string[] {
  const count = new Date(ym.year, ym.month, 0).getDate()
  return Array.from({ length: count }, (_, i) => `${ym.year}-${pad(ym.month)}-${pad(i + 1)}`)
}

const DATE_ATTR = "data-bdrp-date"

/** Datum der Tageszelle unter dem Pointer (auch ausserhalb des Grids: null) */
function dateAtPoint(clientX: number, clientY: number): string | null {
  const el = document.elementFromPoint(clientX, clientY)
  const cell = el?.closest?.(`[${DATE_ATTR}]`)
  return cell?.getAttribute(DATE_ATTR) ?? null
}

interface BusinessDayRangePickerProps {
  /** Zeitraum-Anfang "YYYY-MM-DD" (oder "") */
  from: string
  /** Zeitraum-Ende "YYYY-MM-DD" (oder "") */
  to: string
  onChange: (from: string, to: string) => void
  /** Erster anrechenbarer Tag zaehlt 0,5 (Urlaubs-Halbtag) */
  halfDayStart?: boolean
  /** Letzter anrechenbarer Tag zaehlt 0,5 (Urlaubs-Halbtag) */
  halfDayEnd?: boolean
  className?: string
}

export function BusinessDayRangePicker({
  from,
  to,
  onChange,
  halfDayStart = false,
  halfDayEnd = false,
  className,
}: BusinessDayRangePickerProps) {
  const today = todayIso()

  // Sichtbarer Monat (zweiter Monat daneben ab sm-Breakpoint)
  const [anchor, setAnchor] = useState<YearMonth>(
    () => ymOf(from) ?? ymOf(today) ?? { year: new Date().getFullYear(), month: 1 }
  )
  const second = addMonths(anchor, 1)

  // Picker folgt der Tipp-Eingabe: liegt "from" ausserhalb der sichtbaren
  // Monate, springt der Kalender dorthin.
  useEffect(() => {
    const ym = ymOf(from)
    if (!ym) return
    setAnchor((cur) => (sameYm(ym, cur) || sameYm(ym, addMonths(cur, 1)) ? cur : ym))
  }, [from])

  const primaryDays = useDayInfos(anchor.year, anchor.month)
  const secondaryDays = useDayInfos(second.year, second.month)
  const primaryMap = useMemo(() => toDayMap(primaryDays.data), [primaryDays.data])
  const secondaryMap = useMemo(() => toDayMap(secondaryDays.data), [secondaryDays.data])

  // ---------------------------------------------------------------------------
  // Auswahl: Klick-Klick + echtes Ziehen (PointerEvents)
  // ---------------------------------------------------------------------------

  // Klick-Klick: nach dem ersten Klick wartet der Picker auf das Ende
  const [awaitingEnd, setAwaitingEnd] = useState(false)
  // Laufendes Ziehen (Live-Markierung); moved = Pointer hat die Startzelle verlassen
  const [drag, setDrag] = useState<{ start: string; end: string; moved: boolean } | null>(null)
  const dragRef = useRef(drag)
  dragRef.current = drag
  const callbacksRef = useRef({ onChange, from, awaitingEnd })
  callbacksRef.current = { onChange, from, awaitingEnd }

  function handlePointerDown(e: React.PointerEvent) {
    if (e.button !== 0 && e.pointerType === "mouse") return
    const date = dateAtPoint(e.clientX, e.clientY)
    if (!date) return
    e.preventDefault()
    setDrag({ start: date, end: date, moved: false })
  }

  // Move/Up/Cancel global, damit Ziehen auch ausserhalb des Grids sauber endet
  useEffect(() => {
    if (!drag) return

    const onMove = (e: PointerEvent) => {
      const cur = dragRef.current
      if (!cur) return
      const date = dateAtPoint(e.clientX, e.clientY)
      if (!date || date === cur.end) return
      setDrag({ start: cur.start, end: date, moved: cur.moved || date !== cur.start })
    }

    const onUp = () => {
      const cur = dragRef.current
      if (!cur) return
      const { onChange, from, awaitingEnd } = callbacksRef.current
      if (cur.moved) {
        // Echtes Ziehen: Bereich uebernehmen (Richtung egal)
        const [a, b] = cur.start <= cur.end ? [cur.start, cur.end] : [cur.end, cur.start]
        onChange(a, b)
        setAwaitingEnd(false)
      } else if (awaitingEnd && isIsoDate(from) && cur.start >= from) {
        // Zweiter Klick = Ende
        onChange(from, cur.start)
        setAwaitingEnd(false)
      } else {
        // Erster Klick = Start (Klick vor Start setzt neuen Start)
        onChange(cur.start, cur.start)
        setAwaitingEnd(true)
      }
      setDrag(null)
    }

    const onCancel = () => setDrag(null)

    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onCancel)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onCancel)
    }
  }, [drag !== null]) // eslint-disable-line react-hooks/exhaustive-deps

  // Markierter Bereich: waehrend des Ziehens live, sonst aus den Props
  const range: [string, string] | null = useMemo(() => {
    if (drag) return drag.start <= drag.end ? [drag.start, drag.end] : [drag.end, drag.start]
    if (isIsoDate(from) && isIsoDate(to) && from <= to) return [from, to]
    if (isIsoDate(from)) return [from, from]
    return null
  }, [drag, from, to])

  // ---------------------------------------------------------------------------
  // Live-Zaehler: Betriebstage (isPlannable) im Zeitraum, Halbtage 0,5
  // ---------------------------------------------------------------------------

  const counterQuery = useDayInfosRange(
    isIsoDate(from) ? from : "",
    isIsoDate(to) ? to : ""
  )
  const counter = useMemo(() => {
    const days = counterQuery.data
    if (!days || days.length === 0) return null
    let business = 0
    let skipped = 0
    for (const info of days) {
      if (!info.isPlannable) {
        skipped += 1
        continue
      }
      // Spiegel von leave_service.covered_business_days: 0,5 nur wenn der
      // Randtag selbst anrechenbar ist (start vor end geprueft)
      if (info.date === from && halfDayStart) business += 0.5
      else if (info.date === to && halfDayEnd) business += 0.5
      else business += 1
    }
    return { business, skipped }
  }, [counterQuery.data, from, to, halfDayStart, halfDayEnd])

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  function renderMonth(ym: YearMonth, dayMap: Map<string, DayInfo>, hiddenOnMobile: boolean) {
    const dates = monthDates(ym)
    return (
      <div className={cn("w-full min-w-0", hiddenOnMobile && "hidden sm:block")}>
        <p className="pb-1 text-center text-xs font-semibold">
          {MONTH_NAMES[ym.month - 1]} {ym.year}
        </p>
        <div className="grid grid-cols-7 text-center">
          {WEEKDAYS_SHORT.map((wd) => (
            <div key={wd} className="py-0.5 text-[10px] font-medium text-muted-foreground">
              {wd}
            </div>
          ))}
          {Array.from({ length: weekdayIndex(dates[0]) }, (_, k) => (
            <div key={`pad-${k}`} />
          ))}
          {dates.map((date) => {
            const info = dayMap.get(date)
            const meta = info ? DAY_TYPE_META[info.dayType] : null
            const inRange = range !== null && date >= range[0] && date <= range[1]
            const isStart = inRange && range !== null && date === range[0]
            const isEnd = inRange && range !== null && date === range[1]
            const skippedInRange = inRange && info !== undefined && !info.isPlannable
            const tooltipParts: string[] = []
            if (meta) tooltipParts.push(meta.label)
            if (info?.holidayName) tooltipParts.push(info.holidayName)
            if (info?.note) tooltipParts.push(info.note)
            return (
              <div
                key={date}
                {...{ [DATE_ATTR]: date }}
                title={tooltipParts.join(" — ") || undefined}
                className={cn(
                  "flex h-8 cursor-pointer touch-none select-none items-center justify-center text-xs tabular-nums transition-colors",
                  !inRange && "rounded",
                  !inRange && (meta ? meta.cellClass : "text-foreground hover:bg-accent"),
                  // Durchgehender Balken; Nicht-Betriebstage gedimmt + durchgestrichen
                  inRange && !isStart && !isEnd && "bg-primary/20",
                  inRange && skippedInRange && "text-muted-foreground line-through opacity-70",
                  // Start/Ende betont
                  (isStart || isEnd) &&
                    "bg-primary font-semibold text-primary-foreground",
                  isStart && "rounded-l-md",
                  isEnd && "rounded-r-md",
                  date === today && !inRange && "font-bold ring-1 ring-inset ring-primary"
                )}
              >
                {Number(date.slice(8, 10))}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("rounded-md border p-3", className)}>
      {/* Kopf: Monats-Navigation */}
      <div className="mb-1 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Vorheriger Monat"
          onClick={() => setAnchor((cur) => addMonths(cur, -1))}
        >
          <ChevronLeft />
        </Button>
        <span className="text-xs text-muted-foreground">
          Klick = Start/Ende · Ziehen = Bereich
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Nächster Monat"
          onClick={() => setAnchor((cur) => addMonths(cur, 1))}
        >
          <ChevronRight />
        </Button>
      </div>

      {/* Kalender-Grid(s): Pointer-Handling zentral fuer Cross-Monat-Ziehen */}
      <div className="flex gap-4" onPointerDown={handlePointerDown}>
        {renderMonth(anchor, primaryMap, false)}
        {renderMonth(second, secondaryMap, true)}
      </div>

      {/* Legende */}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t pt-2">
        {DAY_TYPE_ORDER.map((t) => (
          <span key={t} className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <span className={cn("size-2.5 rounded-sm", DAY_TYPE_META[t].dotClass)} />
            {DAY_TYPE_META[t].label}
          </span>
        ))}
      </div>

      {/* Live-Zaehler */}
      <p className="mt-2 text-sm" data-testid="bdrp-counter">
        {counter ? (
          <>
            <span className="font-semibold tabular-nums">{fmtDays(counter.business)}</span>{" "}
            Betriebstage im Zeitraum{" "}
            <span className="text-muted-foreground">
              ({counter.skipped} {counter.skipped === 1 ? "Tag" : "Tage"} übersprungen)
            </span>
          </>
        ) : counterQuery.isLoading ? (
          <span className="text-muted-foreground">Betriebstage werden berechnet …</span>
        ) : (
          <span className="text-muted-foreground">
            Zeitraum wählen — Nicht-Betriebstage werden übersprungen.
          </span>
        )}
      </p>
    </div>
  )
}
